/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../core/models';
import { Layer7ApiService } from '../../../shared/http';
import { L7SticktableModel } from '../../../shared/models/l7.sticktable.model';
import { LogService, NotifyService } from '../../../../../core/services';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-l7-stick-table',
    templateUrl: './l7StickTable.component.html',
    styleUrls: ['./l7StickTable.component.scss']
})

export class L7StickTableComponent extends BaseComponent {
    /**
     * Our table entry of items.
     */
    public tableRows: L7SticktableModel[] = [];

    /**
     * Stores the service's backends.
     */
    public backends: any[];

    /**
     * Set the selected item in the dropdown.
     */
    public selectedBackend: string;

    /**
     * Store our dropdown items.
     */
    public backendsDropdown: any[] = [];

    constructor(
        private activatedRoute: ActivatedRoute,
        private layer7ApiService: Layer7ApiService,
        private logService: LogService,
        private notifyService: NotifyService
    ) {
        super();

        this.tableRows = this.activatedRoute.snapshot.data['entries']['stickTableData'];
        this.backends = this.activatedRoute.snapshot.data['backends'];
        this.createDropdown();
        this.selectedBackend = this.backends[0].id;
    }

    /**
     * Delete an entry based off stick table key.
     * @param key
     */
    public deleteEntry(key: string): void {
        this.layer7ApiService.deleteStickTableItem(this.selectedBackend, 'key', key)
            .subscribe({
                next: (): void => this.refreshTable()
            });
    }

    /**
     * Refresh table data.
     */
    public refreshTable() {
        this.layer7ApiService.getStickTable(this.selectedBackend, 0, 100)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.tableRows = [...data['stickTableData']];
                    this.notifyService.success('Stick table results refreshed');
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('l7sticktable:onClickRefresh()', error.message);
                }
            });
    }

    /**
     * Update the table with data from the selected backend.
     * @param $event
     */
    public updateTableForSelectedBackend($event) {
        if (this.selectedBackend !== $event) {
            this.selectedBackend = $event;
            this.refreshTable();
        }
    }

    /**
     * Create the dropdown.
     * @private
     */
    private createDropdown(): DropdownModel[] {
        if (this.backends) {
            for (const backend of this.backends) {
                this.backendsDropdown.push(<DropdownModel>{ key: backend.id, value: backend.label });
            }
        }

        return this.backendsDropdown;
    }
}
